/**
 * Parametrização (24/10/2026) — "Recebimento da comissão" no cadastro EXISTENTE do parceiro (qualquer papel) e a cópia
 * congelada no envio da Proposta, exibida no Pedido. Preenchida uma única vez por parceiro; em branco = "A confirmar"
 * (nada é calculado por suposição). Alterar o cadastro não muda operações já enviadas: cada versão enviada guarda a sua
 * cópia. A liberação pode vir SUGERIDA pela regra de comissão (frequência ≠ liberação: conferível e ajustável).
 */
import { Ajuda } from './ui.tsx';
import type { IdRegraComissao } from '../../../backend/src/compartilhado/propostas.ts';
import { REGRAS_COMISSAO } from '../../../backend/src/compartilhado/propostas.ts';
import {
  A_CONFIRMAR,
  LIBERACOES_POR_PAGAMENTO,
  liberacaoPelaComissao,
  linhasRegraRecebimento,
  MARCOS_LIBERACAO,
  MARCOS_REPASSE,
  ORIGENS_CONFIRMACAO,
  regraRecebimentoVaziaDe,
  rotuloDe,
  TIPOS_LIBERACAO,
  type Modalidade,
  type RegraRecebimento,
  type RegraRecebimentoCongelada,
  type TipoDias,
} from '../../../backend/src/compartilhado/regra-recebimento.ts';

const modalidadeDoParceiro = (papeis: string[], regra: IdRegraComissao | null): Modalidade => (papeis.includes('Distribuidor') ? 'venda' : papeis.includes('Parceiro de Locação') ? (regra === 'unica_projeto' ? 'locacao_projeto' : 'locacao_mensal') : 'trade_in');
const dias = (v: string) => (v.trim() === '' ? null : Math.min(730, Math.max(0, Math.floor(Number(v.replace(/\D/g, '')) || 0))));

function TipoDiasSelect({ valor, aoMudar, desligado, teste }: { valor: TipoDias | null; aoMudar: (t: TipoDias | null) => void; desligado?: boolean; teste?: string }) {
  return (
    <select value={valor ?? ''} disabled={desligado} onChange={(e) => aoMudar((e.target.value || null) as TipoDias | null)} data-teste={teste}>
      <option value="">{desligado ? '— não se aplica —' : A_CONFIRMAR}</option>
      <option value="corridos">Corridos</option>
      <option value="uteis">Úteis</option>
    </select>
  );
}

export function EditorRegraRecebimento({ valor, aoMudar, papeis, regraComissao }: { valor: RegraRecebimento; aoMudar: (r: RegraRecebimento) => void; papeis: string[]; regraComissao: IdRegraComissao | null }) {
  const sugestao = liberacaoPelaComissao(regraComissao, modalidadeDoParceiro(papeis, regraComissao));
  const nomeRegra = regraComissao ? (REGRAS_COMISSAO.find((r) => r.id === regraComissao)?.nome ?? regraComissao) : '';
  const comDias = valor.liberacao === 'marco_prazo' || valor.liberacao === 'dias_apos_pagamento';
  const comReconhecimento = !valor.liberacao || LIBERACOES_POR_PAGAMENTO.includes(valor.liberacao);
  const set = (p: Partial<RegraRecebimento>) => aoMudar({ ...valor, ...p });
  return (
    <div className="largura-total editor-regra-cobranca" data-teste="editor-regra-recebimento">
      <div className="grade-3" style={{ alignItems: 'start' }}>
        <label className="campo">
          Liberação da comissão
          <select value={valor.liberacao ?? ''} onChange={(e) => set({ liberacao: (e.target.value || null) as RegraRecebimento['liberacao'] })} data-teste="rec-liberacao">
            <option value="">{A_CONFIRMAR}</option>
            {TIPOS_LIBERACAO.map((t) => (
              <option key={t.id} value={t.id}>
                {t.rotulo}
              </option>
            ))}
          </select>
          {sugestao && (
            <span className="texto-secundario" data-teste="rec-sugestao">
              {valor.liberacao === sugestao ? (
                <>Sugerida pela regra de comissão “{nomeRegra}” · conferida ✓</>
              ) : valor.liberacao ? (
                <>Sugestão pela regra de comissão “{nomeRegra}”: {rotuloDe(TIPOS_LIBERACAO, sugestao)} (diferente da escolhida)</>
              ) : (
                <>
                  Sugestão pela regra de comissão “{nomeRegra}”: {rotuloDe(TIPOS_LIBERACAO, sugestao)} ·{' '}
                  <button type="button" className="botao-link" onClick={() => set({ liberacao: sugestao })} data-teste="rec-usar-sugestao">
                    conferi e uso
                  </button>
                </>
              )}
              <Ajuda titulo="Frequência × liberação">Frequência da comissão (quando ela é calculada) e condição de liberação (quando a Inttrix pode cobrar) são conceitos distintos. A sugestão evita preencher duas vezes, mas pode ser alterada e nunca sobrescreve acordos de operações.</Ajuda>
            </span>
          )}
        </label>
        <label className="campo">
          Marco da liberação (quando houver)
          <select value={valor.marcoLiberacao ?? ''} disabled={valor.liberacao !== 'marco_prazo'} onChange={(e) => set({ marcoLiberacao: (e.target.value || null) as RegraRecebimento['marcoLiberacao'] })}>
            <option value="">{valor.liberacao === 'marco_prazo' ? A_CONFIRMAR : '— não se aplica —'}</option>
            {MARCOS_LIBERACAO.map((t) => (
              <option key={t.id} value={t.id}>
                {t.rotulo}
              </option>
            ))}
          </select>
        </label>
        <div className="grade-2">
          <label className="campo">
            Dias da liberação
            <input inputMode="numeric" value={valor.diasLiberacao ?? ''} disabled={!comDias} placeholder={comDias ? A_CONFIRMAR : '— não se aplica —'} onChange={(e) => set({ diasLiberacao: dias(e.target.value) })} />
          </label>
          <label className="campo">
            Tipo de dias
            <TipoDiasSelect valor={valor.tipoDiasLiberacao} aoMudar={(t) => set({ tipoDiasLiberacao: t })} desligado={!comDias} />
          </label>
        </div>
        <div className="grade-2">
          <label className="campo">
            <span>
              Reconhecimento (dias)
              <Ajuda titulo="Reconhecimento do pagamento">Dias entre o pagamento do Cliente ao parceiro e o parceiro reconhecer esse pagamento (ex.: boleto 30 dias + 2 dias = comissão prevista para NF + 32 dias). É diferente do prazo de repasse: se o repasse partir do reconhecimento, os dias não são somados duas vezes.</Ajuda>
            </span>
            <input inputMode="numeric" value={valor.diasReconhecimento ?? ''} disabled={!comReconhecimento} placeholder={comReconhecimento ? A_CONFIRMAR : '— não se aplica —'} onChange={(e) => set({ diasReconhecimento: dias(e.target.value) })} data-teste="rec-dias-reconhecimento" />
          </label>
          <label className="campo">
            Tipo de dias
            <TipoDiasSelect valor={valor.tipoDiasReconhecimento} aoMudar={(t) => set({ tipoDiasReconhecimento: t })} desligado={!comReconhecimento} teste="rec-tipo-reconhecimento" />
          </label>
        </div>
        <label className="campo">
          Confirmação do valor: origem esperada
          <select value={valor.confirmacao ?? ''} onChange={(e) => set({ confirmacao: (e.target.value || null) as RegraRecebimento['confirmacao'] })}>
            <option value="">{A_CONFIRMAR}</option>
            {ORIGENS_CONFIRMACAO.map((t) => (
              <option key={t.id} value={t.id}>
                {t.rotulo}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Repasse: marco inicial
          <select value={valor.marcoRepasse ?? ''} onChange={(e) => set({ marcoRepasse: (e.target.value || null) as RegraRecebimento['marcoRepasse'] })} data-teste="rec-marco-repasse">
            <option value="">{A_CONFIRMAR}</option>
            {MARCOS_REPASSE.map((t) => (
              <option key={t.id} value={t.id}>
                {t.rotulo}
              </option>
            ))}
          </select>
          {valor.marcoRepasse === 'outro' && <input value={valor.marcoRepasseDescricao} maxLength={200} placeholder="Descreva o marco" onChange={(e) => set({ marcoRepasseDescricao: e.target.value })} data-teste="rec-marco-outro" />}
        </label>
        <div className="grade-2">
          <label className="campo">
            Repasse: dias
            <input inputMode="numeric" value={valor.diasRepasse ?? ''} placeholder={A_CONFIRMAR} onChange={(e) => set({ diasRepasse: dias(e.target.value) })} data-teste="rec-dias-repasse" />
          </label>
          <label className="campo">
            Tipo de dias
            <TipoDiasSelect valor={valor.tipoDiasRepasse} aoMudar={(t) => set({ tipoDiasRepasse: t })} teste="rec-tipo-repasse" />
          </label>
        </div>
        <label className="campo largura-total">
          Observações do acordo de repasse
          <input value={valor.observacoes} maxLength={600} placeholder={A_CONFIRMAR} onChange={(e) => set({ observacoes: e.target.value })} />
        </label>
      </div>
    </div>
  );
}

/** Leitura da regra: no cadastro do parceiro (regra atual) ou no Pedido (cópia congelada no envio). */
export function QuadroRegraRecebimento({ regra, titulo, nota, teste = 'regra-recebimento' }: { regra: RegraRecebimento; titulo: string; nota?: string; teste?: string }) {
  const vazia = regraRecebimentoVaziaDe(regra);
  return (
    <section className="bloco-impacto" data-teste={teste}>
      <h3>
        {titulo}
        <Ajuda titulo="Recebimento da comissão">Regra do parceiro, não padrão universal. O que não estiver definido fica “A confirmar”: o HUB não inventa prazo nem data. A Proposta guarda uma cópia no envio; alterar o cadastro depois não muda operações já enviadas.</Ajuda>
      </h3>
      {nota && <p className="texto-secundario sem-margem">{nota}</p>}
      {vazia ? (
        <p className="texto-secundario sem-margem">Nada definido: tudo fica “{A_CONFIRMAR}”.</p>
      ) : (
        <dl className="pares-regra">
          {linhasRegraRecebimento(regra).map((l) => (
            <div key={l.rotulo}>
              <dt>{l.rotulo}</dt>
              <dd>{l.valor}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/** Pedido: cópias congeladas no envio da versão aceita (uma por parceiro). Pedidos anteriores: não há cópia. */
export function RegrasRecebimentoDoPedido({ regras }: { regras?: Record<string, RegraRecebimentoCongelada> }) {
  const lista = Object.values(regras ?? {});
  if (!lista.length) return null;
  const quando = (s: string | null) => (s ? new Date(s).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—');
  return (
    <div className={lista.length > 1 ? 'grade-2' : ''} data-teste="regras-recebimento-pedido">
      {lista.map((x) => (
        <QuadroRegraRecebimento
          key={x.parceiroId}
          regra={x.regra}
          titulo={x.parceiro}
          nota={`Copiada no envio da Proposta em ${quando(x.congeladaEm)}, do cadastro do parceiro atualizado em ${quando(x.cadastroAtualizadoEm)}. Uso interno; não vai para documentos do Cliente.`}
          teste={`regra-recebimento-${x.parceiroId}`}
        />
      ))}
    </div>
  );
}
